import React, { useEffect, useState } from 'react';
import { 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia, 
  Button, 
  Pagination, 
  Box, 
  Chip,
  CircularProgress,
  Alert,
  AlertTitle
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { getBlogPosts } from '../services/blogService';
import { useAuth } from '../context/AuthContext';
import AddIcon from '@mui/icons-material/Add';
import { format } from 'date-fns';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const data = await getBlogPosts(page, 6, selectedTag);
        setPosts(data.posts);
        setTotalPages(data.totalPages);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching blog posts:', err);
        setError('Failed to load blog posts. Please try again later.');
        setLoading(false);
      }
    };

    fetchPosts();
  }, [page, selectedTag]);

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo(0, 0);
  };

  const handleTagClick = (tag) => {
    setSelectedTag(tag === selectedTag ? null : tag);
    setPage(1);
  };

  // Extract all unique tags from posts
  const allTags = [...new Set(posts.flatMap(post => post.tags))];

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Alert severity="info" sx={{ mb: 4 }}>
        <AlertTitle>Important Notice</AlertTitle>
        This information is for educational purposes only and should not replace professional medical advice. 
        Always consult with healthcare providers for personalized diagnosis and treatment.
      </Alert>
      
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Blog
        </Typography>
        {isAuthenticated && (
          <Button 
            component={RouterLink} 
            to="/blog/new" 
            variant="contained" 
            startIcon={<AddIcon />}
          >
            New Post
          </Button>
        )}
      </Box>
      
      {/* Tags filter */}
      {allTags.length > 0 && (
        <Box sx={{ mb: 4, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {allTags.map(tag => (
            <Chip 
              key={tag} 
              label={tag}
              onClick={() => handleTagClick(tag)}
              color={selectedTag === tag ? 'primary' : 'default'}
              clickable
            />
          ))}
          {selectedTag && (
            <Button 
              size="small" 
              onClick={() => setSelectedTag(null)} 
              sx={{ ml: 1 }}
            >
              Clear filter
            </Button>
          )}
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : posts.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6">
            {selectedTag 
              ? `No posts found with tag "${selectedTag}"`
              : 'No blog posts yet'}
          </Typography>
          {isAuthenticated && (
            <Button 
              component={RouterLink} 
              to="/blog/new" 
              variant="contained" 
              sx={{ mt: 2 }}
            >
              Create the first post
            </Button>
          )}
        </Box>
      ) : (
        <>
          <Grid container spacing={4}>
            {posts.map(post => (
              <Grid item xs={12} sm={6} md={4} key={post._id}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: 6
                    }
                  }}
                >
                  {post.featuredImage && (
                    <CardMedia
                      component="img"
                      height="140"
                      image={post.featuredImage}
                      alt={post.title}
                    />
                  )}
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography gutterBottom variant="h5" component="h2" noWrap>
                      {post.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        mb: 2
                      }}
                    >
                      {post.content.replace(/<[^>]*>/g, '').slice(0, 150)}...
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                      <Typography variant="caption" color="text.secondary">
                        {post.createdAt && format(new Date(post.createdAt), 'MMM d, yyyy')}
                      </Typography>
                      <Button 
                        component={RouterLink} 
                        to={`/blog/${post.slug}`} 
                        size="small"
                      >
                        Read More
                      </Button>
                    </Box>
                    
                    {post.tags && post.tags.length > 0 && (
                      <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {post.tags.map(tag => (
                          <Chip 
                            key={tag} 
                            label={tag} 
                            size="small" 
                            onClick={(e) => {
                              e.preventDefault();
                              handleTagClick(tag);
                            }}
                            sx={{ cursor: 'pointer' }}
                          />
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default Blog; 